import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  OverviewAvatarColumn,
  OverviewAvatarActionRow,
  OverviewAvatarName,
  OverviewEditButton,
  OverviewUploadTextButton,
  ProfileFormFooter,
  ProfileInnerCard,
} from './MyProfilePageStyled'
import {
  Avatar,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Upload,
} from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { blue } from '../../color'
import { inject, observer } from 'mobx-react'
import utils from '../../utils'
import validator from '../../validator'
import moment from 'moment'
import ProfileFieldGrid, { ProfileFormGrid } from './ProfileFieldGrid'

const Overview = props => {
  const {
    authenticationStore,
    loadingAnimationStore,
    userStore,
    aclStore,
  } = props

  const { aclAclGroupName } = aclStore
  const { currentUser, currentUserAvatar } = authenticationStore

  const [editing, setEditing] = useState(false)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [isDeletingAvatar, setIsDeletingAvatar] = useState(false)
  const [avatarSrc, setAvatarSrc] = useState(undefined)
  const [form] = Form.useForm()
  const hasCustomAvatar = Boolean(currentUser?.imageId) && Boolean(avatarSrc)
  const displayName = currentUser?.name_uppercase
    ? utils.getNameInCapitalize(currentUser.name_uppercase)
    : currentUser?.username || 'Admin'

  const handleUploadAvatar = async (file, fileList) => {
    if (isDeletingAvatar) return false
    if (file.size / 1000 / 1024 > 3) {
      message.error('Vui lòng chọn file < 3MB')
      return false
    }

    const formData = new FormData()
    formData.append('file', fileList[0])
    setIsUploadingAvatar(true)
    loadingAnimationStore.showSpinner(true)
    try {
      await authenticationStore.updateCurrentUserAvatar(formData)
      await authenticationStore.checkCurrentUser()
      await authenticationStore.getCurrentUserAvatar()
      message.success('Cập nhật avatar thành công')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      setIsUploadingAvatar(false)
      loadingAnimationStore.showSpinner(false)
    }
    return false
  }

  const handleDeleteAvatar = async () => {
    if (isUploadingAvatar || isDeletingAvatar) return
    setIsDeletingAvatar(true)
    loadingAnimationStore.showSpinner(true)
    try {
      await authenticationStore.deleteCurrentUserAvatar()
      setAvatarSrc(undefined)
      try {
        await authenticationStore.checkCurrentUser()
      } catch (refreshErr) {
        console.log(refreshErr)
      }
      message.success('Xóa ảnh đại diện thành công')
    } catch (err) {
      console.log(err)
      message.error(err?.vi || 'Không thể xóa ảnh đại diện')
    } finally {
      setIsDeletingAvatar(false)
      loadingAnimationStore.showSpinner(false)
    }
  }

  const onUpdate = async values => {
    const email = values.email || currentUser?.email
    if (!email) {
      message.error(
        'Không tìm thấy email người dùng. Vui lòng tải lại thông tin tài khoản!'
      )
      return
    }

    loadingAnimationStore.showSpinner(true)
    try {
      const dataToSubmit = {
        ...values,
        email,
        birthday: values.birthday
          ? values.birthday.format('YYYY-MM-DD')
          : null,
      }
      await userStore.updateCurrentUser(dataToSubmit)
      await authenticationStore.checkCurrentUser()
      setEditing(false)
      message.success('Cập nhật thông tin thành công!')
    } catch (err) {
      console.log(err)
      message.error(err?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  useEffect(() => {
    if (!editing || !currentUser) return
    form.setFieldsValue({
      email: currentUser.email,
      phone: currentUser.phone,
      home_phone: currentUser.home_phone,
      gender: currentUser.gender,
      birthday: currentUser.birthday
        ? moment(currentUser.birthday)
        : undefined,
    })
  }, [editing, currentUser, form])

  useEffect(() => {
    const isValidAvatar =
      currentUserAvatar &&
      currentUserAvatar.size > 0 &&
      currentUserAvatar.type &&
      currentUserAvatar.type.startsWith('image/')

    if (!isValidAvatar) {
      setAvatarSrc(undefined)
      return undefined
    }

    const objectUrl = URL.createObjectURL(currentUserAvatar)
    const img = new Image()
    img.onload = () => {
      setAvatarSrc(objectUrl)
    }
    img.onerror = () => {
      setAvatarSrc(undefined)
      URL.revokeObjectURL(objectUrl)
    }
    img.src = objectUrl

    return () => {
      img.onload = null
      img.onerror = null
      URL.revokeObjectURL(objectUrl)
    }
  }, [currentUserAvatar])

  const renderAvatar = () => (
    <OverviewAvatarColumn>
      <Avatar
        size={144}
        style={{
          backgroundColor: hasCustomAvatar ? blue : '#E5E7EB',
          border: '3px solid #0077c8',
          boxShadow: '0 4px 16px rgba(0, 119, 200, 0.16)',
          color: '#FFFFFF',
          fontSize: 64,
        }}
        src={hasCustomAvatar ? avatarSrc : undefined}
        onError={() => {
          setAvatarSrc(undefined)
          return false
        }}>
        <UserOutlined />
      </Avatar>
      <OverviewAvatarActionRow>
        <Upload
          showUploadList={false}
          beforeUpload={handleUploadAvatar}
          disabled={isUploadingAvatar || isDeletingAvatar}>
          <OverviewUploadTextButton>
            <UploadOutlined /> Thay ảnh
          </OverviewUploadTextButton>
        </Upload>
        {hasCustomAvatar && (
          <Popconfirm
            title={
              <>
                <div>Xóa ảnh đại diện?</div>
                <div>Ảnh hiện tại sẽ được xóa và thay bằng ảnh mặc định.</div>
              </>
            }
            okText="Xóa ảnh"
            cancelText="Hủy"
            okButtonProps={{
              danger: true,
              loading: isDeletingAvatar,
            }}
            onConfirm={handleDeleteAvatar}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={isUploadingAvatar || isDeletingAvatar}
              style={{ padding: 0 }}>
              Xóa ảnh
            </Button>
          </Popconfirm>
        )}
      </OverviewAvatarActionRow>
      <OverviewAvatarName>{displayName}</OverviewAvatarName>
    </OverviewAvatarColumn>
  )

  const overviewFields = [
    {
      title: 'Tên đăng nhập',
      value: currentUser?.username,
    },
    {
      title: 'Email',
      value: currentUser?.email,
    },
    {
      title: 'Mã nhân viên',
      value: currentUser?.ma_nv,
    },
    {
      title: 'Số máy nội bộ',
      value: currentUser?.home_phone,
    },
    {
      title: 'Số di động',
      value: currentUser?.phone,
    },
    {
      title: 'Ngày sinh',
      value: currentUser?.birthday
        ? moment(currentUser.birthday).format('DD/MM/YYYY')
        : null,
    },
    {
      title: 'Giới tính',
      value: utils.getGenderName(currentUser?.gender),
    },
    {
      title: 'Phòng ban',
      value: currentUser?.department?.name,
    },
    {
      title: 'Chức danh',
      value: currentUser?.position?.name,
    },
    {
      title: 'Phân quyền',
      value: aclAclGroupName,
    },
  ]

  const overviewFormFields = [
    {
      key: 'username',
      content: (
        <Form.Item label="Tên đăng nhập">
          <Input value={currentUser?.username} disabled />
        </Form.Item>
      ),
    },
    {
      key: 'email',
      content: (
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Vui lòng nhập email!' },
            { validator: validator.validateEmail },
          ]}>
          <Input placeholder="Nhập email" />
        </Form.Item>
      ),
    },
    {
      key: 'ma_nv',
      content: (
        <Form.Item label="Mã nhân viên">
          <Input value={currentUser?.ma_nv} disabled />
        </Form.Item>
      ),
    },
    {
      key: 'home_phone',
      content: (
        <Form.Item name="home_phone" label="Số máy nội bộ">
          <Input placeholder="Nhập số máy nội bộ" />
        </Form.Item>
      ),
    },
    {
      key: 'phone',
      content: (
        <Form.Item
          name="phone"
          label="Số di động"
          rules={[{ validator: validator.validatePhoneNumber }]}>
          <Input placeholder="Nhập số di động" />
        </Form.Item>
      ),
    },
    {
      key: 'birthday',
      content: (
        <Form.Item name="birthday" label="Ngày sinh">
          <DatePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            placeholder="Chọn ngày sinh"
          />
        </Form.Item>
      ),
    },
    {
      key: 'gender',
      content: (
        <Form.Item name="gender" label="Giới tính">
          <Select placeholder="Chọn giới tính">
            <Select.Option value={1}>Nam</Select.Option>
            <Select.Option value={0}>Nữ</Select.Option>
            <Select.Option value={2}>Khác</Select.Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      key: 'department',
      content: (
        <Form.Item label="Phòng ban">
          <Input value={currentUser?.department?.name} disabled />
        </Form.Item>
      ),
    },
    {
      key: 'position',
      content: (
        <Form.Item label="Chức danh">
          <Input value={currentUser?.position?.name} disabled />
        </Form.Item>
      ),
    },
    {
      key: 'acl',
      content: (
        <Form.Item label="Phân quyền">
          <Input value={aclAclGroupName} disabled />
        </Form.Item>
      ),
    },
  ]

  const renderViewMode = () => (
    <div style={{ position: 'relative' }}>
      <OverviewEditButton
        type="button"
        onClick={() => {
          setEditing(true)
        }}>
        <EditOutlined />
      </OverviewEditButton>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={6} lg={5}>
          {renderAvatar()}
        </Col>
        <Col xs={24} md={18} lg={19}>
          <ProfileFieldGrid fields={overviewFields} />
        </Col>
      </Row>
    </div>
  )

  const renderEditMode = () => (
    <ProfileInnerCard>
      <Row gutter={[32, 24]}>
        <Col xs={24} md={6} lg={5}>
          {renderAvatar()}
        </Col>
        <Col xs={24} md={18} lg={19}>
          <Form form={form} layout="vertical" onFinish={onUpdate}>
            <ProfileFormGrid fields={overviewFormFields} />

            <Divider style={{ margin: '24px 0' }} />
            <ProfileFormFooter style={{ marginTop: 0 }}>
              <Button
                onClick={() => setEditing(false)}
                style={{ borderRadius: 8, padding: '0 24px', minHeight: 40 }}>
                Hủy bỏ
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{ borderRadius: 8, padding: '0 24px', minHeight: 40 }}>
                Cập nhật
              </Button>
            </ProfileFormFooter>
          </Form>
        </Col>
      </Row>
    </ProfileInnerCard>
  )

  return editing ? renderEditMode() : renderViewMode()
}

Overview.propTypes = {
  authenticationStore: PropTypes.shape({
    currentUser: PropTypes.shape({
      email: PropTypes.string,
      imageId: PropTypes.string,
      phone: PropTypes.string,
      home_phone: PropTypes.string,
      gender: PropTypes.number,
      username: PropTypes.string,
      ma_nv: PropTypes.string,
      birthday: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name_uppercase: PropTypes.string,
      department: PropTypes.shape({
        name: PropTypes.string,
      }),
      position: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    currentUserAvatar: PropTypes.object,
    updateCurrentUserAvatar: PropTypes.func.isRequired,
    deleteCurrentUserAvatar: PropTypes.func.isRequired,
    getCurrentUserAvatar: PropTypes.func.isRequired,
    checkCurrentUser: PropTypes.func.isRequired,
  }).isRequired,
  loadingAnimationStore: PropTypes.shape({
    showSpinner: PropTypes.func.isRequired,
  }).isRequired,
  userStore: PropTypes.shape({
    updateCurrentUser: PropTypes.func.isRequired,
  }).isRequired,
  aclStore: PropTypes.shape({
    aclAclGroupName: PropTypes.string,
  }).isRequired,
}

export default inject(
  'authenticationStore',
  'loadingAnimationStore',
  'userStore',
  'aclStore'
)(observer(Overview))
